import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoFede from '../assets/logo_fede.jpg'
import bulletinRecto from '../assets/bulletins_recto_2025.jpg';
import bulletinVerso from '../assets/bulletins_verso_2025.jpg';

function useMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handle = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handle);
        return () => window.removeEventListener('resize', handle);
    }, []);
    return isMobile;
}

// Préfixes d'âge (PP avant P pour éviter le court-circuit)
const AGE_PREFIXES = ['PP', 'P', 'B', 'M', 'C', 'J', 'V'];

/**
 * Retourne 'M' ou 'F' d'après le nom de catégorie.
 * Règle : si un préfixe d'âge est présent, la lettre qui suit est le genre.
 * Sinon (senior), la première lettre est le genre.
 * Exemples : M60→M, F60→F, MF52→F, MM60→M, CM60→M, CF52→F, PPF30→F
 */
function getCategoryGender(catName) {
    if (!catName) return null;
    const upper = catName.toUpperCase();
    for (const prefix of AGE_PREFIXES) {
        if (upper.startsWith(prefix)) {
            const rest = upper.slice(prefix.length);
            if (rest.startsWith('M') || rest.startsWith('F')) {
                return rest[0];
            }
            // Ce préfixe ne précède pas M/F → essayer le suivant
        }
    }
    // Pas de préfixe d'âge reconnu → genre senior = première lettre
    if (upper.startsWith('M') || upper.startsWith('F')) return upper[0];
    return null;
}

export default function ExportDocument({ order, currentRings, allAthletes }) {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewTitle, setPreviewTitle] = useState("Sélectionnez un document");
    const [selectedRing, setSelectedRing] = useState(1);
    const isMobile = useMobile();

    // --- UTILITAIRES ---
    const getAthleteDisplay = useCallback((id, placeholder, m) => {
        // SÉCURITÉ : Si l'objet match m n'est pas passé ou est vide
        if (!m) return placeholder || "";
        // 1. Si on a un placeholder (ex: "Vainqueur demi-finale")
        if (!id && placeholder) {
            // On cherche si c'est une demande de vainqueur
            if (/vainqueur/i.test(placeholder)) {
                // RECHERCHE INTELLIGENTE :
                // On cherche dans "order" le match de la MÊME catégorie qui était la demi-finale
                const sourceMatch = order.find(match => 
                    match.category_name === m.category_name && 
                    /demi/i.test(match.poule_label || "") &&
                    match.id !== m.id // On ne veut pas que le match se trouve lui-même
                );

                if (sourceMatch) {
                    return `V${sourceMatch.match_ref}`;
                }
            }
            return placeholder;
        }

        // 2. Si pas d'ID
        if (!id) return "À déterminer";

        // 3. Recherche de l'athlète
        const athlete = allAthletes?.find(a => String(a.id) === String(id));
        if (!athlete) return "Inconnu";
        const nom = (athlete.nom || athlete.name || "").toUpperCase();
        const prenom = athlete.prenom || athlete.firstname || "";
        return `${nom} ${prenom}`.trim();
    }, [allAthletes, order]); // On ajoute "order" dans les dépendances


    const getAthleteClub = useCallback((id) => {
        const athlete = allAthletes?.find(a => String(a.id) === String(id));
        return athlete?.club || "";
    }, [allAthletes]);

    const handleOutput = useCallback((doc, fileName, isPreview) => {
        if (isPreview) {
            const blob = doc.output('bloburl');
            setPreviewUrl(blob);
            setPreviewTitle(fileName);
        } else {
            doc.save(fileName);
        }
    }, []);

    // --- FONCTION 1 : FEUILLE DE PESÉE ---
    const generatePesee = useCallback((genre, isPreview = false) => {
        const doc = new jsPDF({ orientation: 'landscape' });
        const athletes = [];
        const seen = new Set();

        order.forEach(m => {
            [m.athlete1_id, m.athlete2_id].forEach(id => {
                // On ne prend que les athlètes réels (id présent) du bon genre
                if (id && !seen.has(id) && getCategoryGender(m.category_name) === genre) {
                    // On passe {} comme 3ème argument pour valider la sécurité "if(!m)" 
                    // sans être un vrai match de compétition
                    const athleteName = getAthleteDisplay(id, "Nom inconnu", {}); 
                    
                    athletes.push({ 
                        name: athleteName, 
                        club: getAthleteClub(id), 
                        cat: m.category_name 
                    });
                    seen.add(id);
                }
            });
        });

        // --- PAGE 1 : ADMINISTRATIVE ---
        try { 
            doc.addImage(logoFede, 'JPEG', 133, 10, 30, 35); 
        } catch (e) {
            console.warn("Logo non disponible pour le PDF");
        }

        doc.setFontSize(8);
        doc.text("FEDERATION FRANCAISE DE SAVATE boxe française & DISCIPLINES ASSOCIEES", 148, 50, { align: 'center' });
        doc.text("49 rue du fg Poissonnière 75009 PARIS - 01 53 24 60 60 - fax : 01 53 24 60 70", 148, 54, { align: 'center' });
        doc.text("site : http://www.ffsavate.com", 148, 58, { align: 'center' });

        doc.setFontSize(22);
        doc.text("FEUILLE DE PESEE", 148, 75, { align: 'center' });
        
        const infoY = 100;
        doc.setFontSize(11);
        doc.text("Type de réunion : ___________________________", 40, infoY);
        doc.text("Niveau : ____________________________________", 40, infoY + 15);
        doc.text("Date : ______________________________________", 40, infoY + 30);
        doc.text("Salle / Ville : ____________________________", 160, infoY);
        doc.text("Début pesée : ________ Fin pesée : ________", 160, infoY + 15);

        // --- TRI ET GROUPES ---
        // Ordre d'âge : PP < P < B < M < C < J < Senior (pas de préfixe) < V
        const AGE_ORDER = ['PP', 'P', 'B', 'M', 'C', 'J', '', 'V'];
        const getAgePrefix = (catName) => {
            const upper = (catName || '').toUpperCase();
            for (const prefix of ['PP', 'P', 'B', 'M', 'C', 'J', 'V']) {
                if (upper.startsWith(prefix)) {
                    // Vérifier que c'est bien un préfixe d'âge (suivi de M/F ou d'un chiffre via le genre)
                    const rest = upper.slice(prefix.length);
                    if (rest.startsWith('M') || rest.startsWith('F')) return prefix;
                }
            }
            return ''; // Senior
        };
        const getWeight = (catName) => {
            const match = (catName || '').match(/(\d+)/);
            return match ? parseInt(match[1]) : 0;
        };
        athletes.sort((a, b) => {
            const ageA = AGE_ORDER.indexOf(getAgePrefix(a.cat));
            const ageB = AGE_ORDER.indexOf(getAgePrefix(b.cat));
            if (ageA !== ageB) return ageA - ageB;
            return getWeight(a.cat) - getWeight(b.cat);
        });
        
        const athleteGroups = [];
        for (let i = 0; i < athletes.length; i += 14) { 
            athleteGroups.push(athletes.slice(i, i + 14)); 
        }
        if (athleteGroups.length === 0) athleteGroups.push([]);

        athleteGroups.forEach((group) => {
            doc.addPage();
            doc.setFontSize(10);
            doc.text("FFSBF&DA - FEUILLE DE PESEE ET DE CONTRÔLE MEDICAL", 148, 10, { align: 'center' });

            const tableRows = group.map(a => [
                a.cat, 
                `${a.name}\n${a.club}`, 
                "", "", "", "", "", "", ""
            ]);
            
            // On complète les lignes vides pour atteindre 14 lignes par page (esthétique)
            while(tableRows.length < 14) {
                tableRows.push(["", "", "", "", "", "", "", "", ""]);
            }

            autoTable(doc, {
                startY: 15,
                head: [["Catégorie\nPoids", "Nom-Prénom\nClub", "Licence\nPasseport", "Age", "Série", "Poids\n1  2  3", "Pass.\nMédical", "Tens.", "Autorisation\nObservation"]],
                body: tableRows,
                theme: 'grid',
                styles: { 
                    fontSize: 7.5, 
                    cellPadding: 1.5, 
                    minCellHeight: 10, 
                    halign: 'center', 
                    valign: 'middle', 
                    lineColor: [0,0,0], 
                    lineWidth: 0.5,
                    textColor: [0,0,0]
                },
                headStyles: { 
                    fillColor: [0, 0, 0],
                    textColor: [255, 255, 255]
                }
            });

            const pageHeight = doc.internal.pageSize.height;
            const sigY = pageHeight - 32;
            doc.setFontSize(9);
            doc.rect(20, sigY, 115, 25); 
            doc.text("Le Délégué Officiel Pesée (nom et signature)", 25, sigY + 5);
            doc.rect(162, sigY, 115, 25);
            doc.text("Le Médecin (nom et signature)", 167, sigY + 6);
        });

        handleOutput(doc, `Feuille_Pesee_${genre}.pdf`, isPreview);
    }, [order, getAthleteDisplay, getAthleteClub, handleOutput]);

    // --- FONCTION 2 : ORDRE DE PASSAGE ---
    const generateODP = useCallback((ringId, isPreview = false) => {
        const doc = new jsPDF({ orientation: 'portrait' });
        
        // Filtrage et Tri numérique
        const ringMatches = order
            .filter(m => m.ring_id === ringId)
            .sort((a, b) => {
                // On extrait les chiffres du match_ref (ex: "Assaut 10" -> 10)
                const numA = parseInt(String(a.match_ref).replace(/\D/g, '')) || 0;
                const numB = parseInt(String(b.match_ref).replace(/\D/g, '')) || 0;
                return numA - numB;
            });

        doc.setFontSize(22);
        doc.text("ORDRE DE PASSAGE", 105, 20, { align: 'center' }); 
        doc.setFontSize(14);
        doc.text(`RING N° ${ringId}`, 105, 30, { align: 'center' }); 

        const rows = ringMatches.map((m) => [
            m.match_ref || "-",
            `${m.category_name}\n(${m.poule_label || m.type})`,
            getAthleteDisplay(m.athlete1_id, m.placeholder1, m),
            "VS",
            getAthleteDisplay(m.athlete2_id, m.placeholder2, m)
        ]);
        // Clubs séparés pour les colonnes rouge/bleu (utilisés dans didDrawCell)
        const clubs = ringMatches.map((m) => [
            getAthleteClub(m.athlete1_id),
            getAthleteClub(m.athlete2_id)
        ]);

        autoTable(doc, {
            startY: 40,
            head: [["N°", "Catégorie / Type", "Coin ROUGE", "", "Coin BLEU"]],
            body: rows,
            theme: 'grid',
            styles: { fontSize: 11, cellPadding: 4, halign: 'center', valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.2 },
            headStyles: { fillColor: [44, 82, 130], halign: 'center' },
            columnStyles: {
                0: { cellWidth: 15, fontStyle: 'bold' },
                1: { cellWidth: 50 },
                3: { cellWidth: 15, fontStyle: 'bold', textColor: [150, 150, 150] }
            },
            didParseCell: (data) => {
                if (data.section === 'body') {
                    if (data.column.index === 2) {
                        data.cell.styles.textColor = [200, 0, 0];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    if (data.column.index === 4) {
                        data.cell.styles.textColor = [0, 0, 200];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    // Agrandir les cellules athlète pour laisser de la place au club
                    if (data.column.index === 2 || data.column.index === 4) {
                        data.cell.styles.minCellHeight = 16;
                    }
                }
            },
            didDrawCell: (data) => {
                if (data.section !== 'body') return;
                if (data.column.index !== 2 && data.column.index !== 4) return;
                const rowIdx = data.row.index;
                const club = data.column.index === 2 ? clubs[rowIdx]?.[0] : clubs[rowIdx]?.[1];
                if (!club) return;
                const color = data.column.index === 2 ? [200, 0, 0] : [0, 0, 200];
                doc.setFontSize(8);
                doc.setTextColor(...color);
                doc.setFont(undefined, 'normal');
                doc.text(club, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height - 3, { align: 'center' });
                // Remettre les styles par défaut pour les cellules suivantes
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');
            }
        });
        handleOutput(doc, `ODP_Ring_${ringId}.pdf`, isPreview);
    }, [order, getAthleteDisplay, getAthleteClub, handleOutput]);

    // --- FONCTION 3 : FEUILLE DE RÉUNION ---
    const generateReunion = useCallback((ringId, isPreview = false) => {
        const doc = new jsPDF({ orientation: 'landscape' });
        
        // 1. Filtrage et Tri numérique (important pour la cohérence des numéros)
        const ringMatches = order
            .filter(m => m.ring_id === ringId)
            .sort((a, b) => {
                const numA = parseInt(String(a.match_ref).replace(/\D/g, '')) || 0;
                const numB = parseInt(String(b.match_ref).replace(/\D/g, '')) || 0;
                return numA - numB;
            });

        const matchGroups = [];
        for (let i = 0; i < ringMatches.length; i += 10) { 
            matchGroups.push(ringMatches.slice(i, i + 10)); 
        }

        // --- PAGE 1 : ADMINISTRATIVE ---
        doc.setFontSize(20);
        doc.text("FEUILLE DE RÉUNION", 148, 20, { align: 'center' });
        doc.setFontSize(12);
        doc.text("FEDERATION FRANCAISE DE SAVATE - Boxe Française", 148, 30, { align: 'center' });
        doc.setFontSize(10);
        doc.text("TYPE DE REUNION : ________________________________________", 20, 50);
        doc.text(`RING N° : ${ringId}`, 148, 50, { align: 'center' });
        doc.text("DATE - LIEU : ____________________________________________", 20, 65);
        doc.rect(20, 80, 257, 40);
        doc.text("OBSERVATIONS DU D.O. / RECLAMATIONS :", 25, 90);
        doc.rect(20, 130, 130, 30);
        doc.text("OBSERVATIONS MEDICALES :", 25, 140);
        doc.text("NOM DU MEDECIN - SIGNATURE", 25, 180);
        doc.text("NOM DU DO - SIGNATURE", 210, 180);

        // --- PAGES DE MATCHS (10 PAR PAGE) ---
        matchGroups.forEach((group, pageIdx) => {
            doc.addPage();
            const tableRows = [];
            
            for (let i = 0; i < 10; i++) {
                const match = group[i];
                const assautNum = (pageIdx * 10) + i + 1;
                const infoPoule = match?.poule_label || match?.type || "";
                const categoryLabel = match ? `${match.category_name}\nAssaut n°${assautNum}\n${infoPoule ? `(${infoPoule})` : ""}` : `\nAssaut n°${assautNum}\n`;

                // Ligne Rouge
                tableRows.push([
                    { content: categoryLabel, rowSpan: 2, styles: { halign: 'center', valign: 'middle', fontSize: 7 } },
                    "R", 
                    getAthleteDisplay(match?.athlete1_id, match?.placeholder1, match), // Ajout de match ici
                    getAthleteClub(match?.athlete1_id),
                    "", "", "", "", "", "", "", ""
                ]);
                
                // Ligne Bleue
                tableRows.push([
                    "B", 
                    getAthleteDisplay(match?.athlete2_id, match?.placeholder2, match), // Ajout de match ici
                    getAthleteClub(match?.athlete2_id),
                    "", "", "", "", "", "", "", ""
                ]);
            }

            autoTable(doc, {
                startY: 8, 
                head: [["Catégorie / Poule", "C.", "Nom et Prénom", "Club", "Arb.", "J1", "J2", "J3", "J4", "J5", "Chr.", "Décis."]],
                body: tableRows,
                theme: 'grid',
                styles: { fontSize: 7.5, cellPadding: 0.8, minCellHeight: 8.8, valign: 'middle', halign: 'center', lineColor:[0,0,0], lineWidth:0.5 },
                headStyles: { fillColor: [0, 0, 0], halign: 'center' },
                columnStyles: {
                    0: { cellWidth: 25 }, // Réduit au max pour gagner de la place
                    1: { cellWidth: 6 },  // Coin ultra-serré
                    2: { cellWidth: 45 }, 
                    3: { cellWidth: 35 }, 
                    // Colonnes de jugement
                    4: { cellWidth: 23 }, // Arbitre un peu plus large
                    5: { cellWidth: 18 }, // J1
                    6: { cellWidth: 18 }, // J2
                    7: { cellWidth: 18 }, // J3
                    8: { cellWidth: 18 }, // J4
                    9: { cellWidth: 18 }, // J5
                },
                didParseCell: (data) => {
                    if (data.section === 'body') {
                        // On identifie la ligne : R (pair dans tableRows) ou B (impair)
                        // Mais autoTable gère ses propres index, donc on regarde la cellule "C." (index 1)
                        // Ou plus simplement on regarde la valeur de la cellule de la colonne "C."
                        const rowData = data.row.raw;
                        const colorIndicator = rowData[1]; // Contient "R" ou "B" (attention si fusionné, rowData peut varier)
                        
                        // On cible les colonnes : C. (1), Nom (2) et Club (3)
                        if ([1, 2, 3].includes(data.column.index)) {
                            // Si la ligne contient "R" (Coin Rouge)
                            if (colorIndicator === "R" || data.row.index % 2 === 0) {
                                data.cell.styles.textColor = [200, 0, 0]; // Rouge sombre
                                data.cell.styles.fontStyle = 'bold';
                            } 
                            // Si la ligne contient "B" (Coin Bleu)
                            else {
                                data.cell.styles.textColor = [0, 0, 200]; // Bleu sombre
                                data.cell.styles.fontStyle = 'bold';
                            }
                        }
                    }
                }
            });
        });
        
        handleOutput(doc, `Feuille_Reunion_Ring_${ringId}.pdf`, isPreview);
    }, [order, getAthleteDisplay, getAthleteClub, handleOutput]);

    // --- FONCTION 4 : BULLETINS ---
    const generateBulletins = useCallback((ringId, isPreview = false) => {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        
        // 1. Tri numérique pour avoir les bulletins dans l'ordre des assauts
        const matches = order
            .filter(m => m.ring_id === ringId)
            .sort((a, b) => {
                const numA = parseInt(String(a.match_ref).replace(/\D/g, '')) || 0;
                const numB = parseInt(String(b.match_ref).replace(/\D/g, '')) || 0;
                return numA - numB;
            });

        matches.forEach((match, index) => {
            // --- 2. Appel à getAthleteDisplay avec l'objet match complet ---
            const nomRouge = getAthleteDisplay(match.athlete1_id, match.placeholder1, match);
            const nomBleu = getAthleteDisplay(match.athlete2_id, match.placeholder2, match);

            if (index > 0) doc.addPage();
            
            // --- 3. Calcul de la taille de police dynamique ---
            let fSize = 9; 
            doc.setFontSize(fSize);
            // On vérifie la largeur des deux noms pour s'assurer qu'ils rentrent dans les cases
            while (fSize > 4 && (doc.getTextWidth(nomRouge) > 26 || doc.getTextWidth(nomBleu) > 26)) {
                fSize -= 0.5;
                doc.setFontSize(fSize);
            }

            // --- 4. Génération RECTO ---
            doc.addImage(bulletinRecto, 'JPEG', 0, 0, 210, 297);
            doc.setFont(undefined, 'bold');
            
            // Nom Rouge
            doc.setTextColor(200, 0, 0);
            doc.text(nomRouge, 37, 37.5);
            doc.text(nomRouge, 139, 37.5); 
            doc.text(nomRouge, 37, 181);
            doc.text(nomRouge, 140, 203);  
            
            // Nom Bleu
            doc.setTextColor(0, 0, 200);
            doc.text(nomBleu, 66, 37.5);
            doc.text(nomBleu, 168, 37.5);
            doc.text(nomBleu, 66, 181);
            doc.text(nomBleu, 140, 211);

            // --- 5. Génération VERSO ---
            doc.addPage();
            doc.addImage(bulletinVerso, 'JPEG', 0, 0, 210, 297);
            doc.setTextColor(200, 0, 0);
            doc.text(nomRouge, 37, 15);
            doc.text(nomRouge, 139, 15); 
            doc.text(nomRouge, 37, 159);
            doc.text(nomRouge, 139, 159);

            doc.setTextColor(0, 0, 200);
            doc.text(nomBleu, 66, 15);
            doc.text(nomBleu, 168, 15);
            doc.text(nomBleu, 66, 159);
            doc.text(nomBleu, 168, 159);
        });

        handleOutput(doc, `Bulletins_Ring_${ringId}.pdf`, isPreview);
    }, [order, getAthleteDisplay, handleOutput]);

    // --- EFFET PRÉVISUALISATION ---
    useEffect(() => {
        if (order && order.length > 0) {
            generateReunion(selectedRing, true);
        }
    }, [selectedRing, order, generateReunion]);

    const btnStyle = {
        flex: 1, padding: '10px 6px', fontSize: '13px', border: '1px solid #cbd5e0',
        backgroundColor: 'white', cursor: 'pointer', borderRadius: '0'
    };

    const controlsPanel = (
        <div style={{ backgroundColor: '#f8fafc', padding: isMobile ? '16px' : '20px', borderRadius: '12px', border: '1px solid #e2e8f0', overflowY: isMobile ? 'visible' : 'auto', width: isMobile ? '100%' : '380px', flexShrink: 0, boxSizing: 'border-box' }}>
            <h3 style={{ fontSize: '17px', fontWeight: 'bold', marginBottom: '16px', margin: '0 0 16px 0' }}>Gestion des Exports</h3>

            {/* Pesée */}
            <div style={{ marginBottom: '20px', padding: '14px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', marginBottom: '10px', textTransform: 'uppercase' }}>Pesée (global)</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <button onClick={() => generatePesee('M', !isMobile)} style={{ ...btnStyle, padding: '10px', borderRadius: '6px' }}>
                        {isMobile ? '💾 Hommes (PDF)' : '👁️ Hommes'}
                    </button>
                    {!isMobile && <button onClick={() => generatePesee('M', false)} style={{ ...btnStyle, padding: '10px', borderRadius: '6px' }}>💾 PDF</button>}
                    <button onClick={() => generatePesee('F', !isMobile)} style={{ ...btnStyle, padding: '10px', borderRadius: '6px' }}>
                        {isMobile ? '💾 Femmes (PDF)' : '👁️ Femmes'}
                    </button>
                    {!isMobile && <button onClick={() => generatePesee('F', false)} style={{ ...btnStyle, padding: '10px', borderRadius: '6px' }}>💾 PDF</button>}
                </div>
            </div>

            {/* Sélecteur de ring */}
            <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>Sélectionner un ring</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(55px, 1fr))', gap: '6px' }}>
                    {Array.from({ length: currentRings }, (_, i) => i + 1).map(id => (
                        <button key={id} onClick={() => setSelectedRing(id)} style={{ padding: '10px 6px', backgroundColor: selectedRing === id ? '#3182ce' : '#fff', color: selectedRing === id ? '#fff' : '#2d3748', border: `1px solid ${selectedRing === id ? '#3182ce' : '#cbd5e0'}`, cursor: 'pointer', borderRadius: '6px', fontWeight: 'bold', fontSize: '13px' }}>
                            R{id}
                        </button>
                    ))}
                </div>
            </div>

            {/* Documents par ring */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                    { label: `Feuille de Réunion — Ring ${selectedRing}`, gen: generateReunion },
                    { label: `Ordre de Passage — Ring ${selectedRing}`, gen: generateODP },
                    { label: `Bulletins — Ring ${selectedRing}`, gen: generateBulletins }
                ].map(({ label, gen }) => (
                    <div key={label} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{ padding: '8px 12px', backgroundColor: '#f1f5f9', fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>{label}</div>
                        <div style={{ display: 'flex' }}>
                            {!isMobile && (
                                <button onClick={() => gen(selectedRing, true)} style={{ ...btnStyle, borderTop: '1px solid #e2e8f0' }}>👁️ Aperçu</button>
                            )}
                            <button onClick={() => gen(selectedRing, false)} style={{ ...btnStyle, borderTop: '1px solid #e2e8f0' }}>💾 Télécharger PDF</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    if (isMobile) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {controlsPanel}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', gap: '20px', height: '90vh' }}>
            {controlsPanel}
            <div style={{ flex: 1, backgroundColor: '#f1f5f9', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '10px 20px', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', fontWeight: 'bold', fontSize: '14px' }}>Aperçu : {previewTitle}</div>
                {previewUrl
                    ? <iframe src={previewUrl} width="100%" height="100%" title="Preview" style={{ border: 'none' }} />
                    : <div style={{ textAlign: 'center', marginTop: '50px', color: '#a0aec0' }}>Sélectionnez un document pour l'aperçu</div>
                }
            </div>
        </div>
    );
}